import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { Container, Row, Col, Button, Form, Spinner, Alert, Card } from "react-bootstrap";
import Image from "react-bootstrap/Image";
import { isLoggedIn, getSubsite, getUser, isAdmin, isSuperAdmin } from '../reducers/auth';
import { addPrintItemToOrder, cart, toggleShowCart } from '../reducers/cart';
import PrintItemService from "../services/printitem.service";
import UserService from "../services/user.service";

const PRICING_TYPES = {
  FLAT: 1,
  QUANTITY: 2,
  BILL_LATER: 3
};

const apiUrl = process.env.REACT_APP_LOCALHOST_URL;

const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  if (url.includes('uploads')) return apiUrl + url;
  return url;
};

const PrintItemDetails = () => {
  const { printItemId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const loginStatus = useSelector(isLoggedIn);
  const currentSubsite = useSelector(getSubsite);
  const cartItems = useSelector(cart);
  const user = useSelector(getUser);
  const adminLoggedIn = useSelector(isAdmin);
  const superAdminLoggedIn = useSelector(isSuperAdmin);
  const isAdminUser = adminLoggedIn || superAdminLoggedIn;

  const [printItem, setPrintItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(null);
  const [adding, setAdding] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('Item added to cart!');
  const [alertVariant, setAlertVariant] = useState('success');
  const [personalizationFields, setPersonalizationFields] = useState([]);
  const [personalizationValues, setPersonalizationValues] = useState({});
  const [userAddresses, setUserAddresses] = useState([]);
  const [addressSelections, setAddressSelections] = useState({});
  const [impersonatedUser, setImpersonatedUser] = useState(null);
  const [impersonatedAddresses, setImpersonatedAddresses] = useState([]);
  const [userSearch, setUserSearch] = useState("");
  const [userSearchResults, setUserSearchResults] = useState([]);
  const [searchingUsers, setSearchingUsers] = useState(false);
  const [orderBlank, setOrderBlank] = useState(false);

  const effectiveUser = impersonatedUser || user;
  const effectiveAddresses = impersonatedUser ? impersonatedAddresses : userAddresses;

  useEffect(() => {
    loadPrintItem();
  }, [printItemId]);

  // Load personalization once both the print item (with styleId) and subsite are available
  useEffect(() => {
    if (printItem?.styleId && currentSubsite?.id) {
      loadPersonalizationFields(printItem.styleId, currentSubsite.id);
    }
  }, [printItem?.styleId, currentSubsite?.id]);

  const loadPrintItem = async () => {
    try {
      setLoading(true);
      const item = await PrintItemService.getPublicPrintItemById(printItemId);
      setPrintItem(item);
      setSelectedImage(item.imageUrl);

      // Set initial quantity for quantity pricing
      if (item.pricingType === PRICING_TYPES.QUANTITY && item.print_item_quantity_prices?.length > 0) {
        setQuantity(item.print_item_quantity_prices[0].quantity);
      }
    } catch (err) {
      console.error("Error loading print item:", err);
      setError("Failed to load item");
    } finally {
      setLoading(false);
    }
  };

  const formatAddress = (addr) => {
    if (!addr) return '';
    return [addr.street, addr.street2, addr.city, addr.state, addr.zipCode]
      .filter(p => p && p.toString().trim().length > 0)
      .join(', ');
  };

  const getProfileValue = (profileFieldName, currentUser, selectedAddress) => {
    if (!currentUser) return '';
    switch (profileFieldName) {
      case 'firstname': return currentUser.firstname || '';
      case 'lastname': return currentUser.lastname || '';
      case 'fullname': return [currentUser.firstname, currentUser.lastname].filter(Boolean).join(' ');
      case 'email': return currentUser.email || '';
      case 'phone': return currentUser.phone || '';
      case 'address': return formatAddress(selectedAddress);
      default: return '';
    }
  };

  const loadPersonalizationFields = async (styleId, subsiteId) => {
    try {
      const response = await fetch(
        process.env.REACT_APP_API_URL + "/personalization/style/" + styleId + "?subsiteId=" + subsiteId
      );
      const fields = await response.json();
      const processedFields = (fields || []).map(f => ({
        ...f,
        options: f.options ? (typeof f.options === 'string' ? JSON.parse(f.options) : f.options) : []
      }));
      setPersonalizationFields(processedFields);
      const initialValues = {};
      processedFields.forEach(field => { initialValues[field.id] = ''; });
      setPersonalizationValues(initialValues);
      setAddressSelections({});
    } catch (err) {
      console.error("Error loading personalization fields:", err);
    }
  };

  // Load user addresses when any personalization field is mapped to address (own user only)
  useEffect(() => {
    const needsAddresses = (personalizationFields || []).some(f => f.userProfileField === 'address');
    if (needsAddresses && user && userAddresses.length === 0) {
      UserService.getAddresses()
        .then(resp => setUserAddresses((resp && resp.data) || []))
        .catch(e => console.error("Error loading user addresses:", e));
    }
  }, [personalizationFields, user]);

  // Apply pre-fills using the effective user (admin may impersonate another user)
  useEffect(() => {
    if (!personalizationFields || personalizationFields.length === 0) return;
    const newValues = { ...personalizationValues };
    const newAddrSelections = { ...addressSelections };
    let changed = false;
    personalizationFields.forEach(field => {
      if (!field.userProfileField) return;
      if (field.userProfileField === 'address') {
        if (!newAddrSelections[field.id] && effectiveAddresses.length > 0) {
          const defaultAddr = effectiveAddresses[0];
          newAddrSelections[field.id] = defaultAddr.id;
          newValues[field.id] = formatAddress(defaultAddr);
          changed = true;
        }
      } else if (!newValues[field.id]) {
        const v = getProfileValue(field.userProfileField, effectiveUser, null);
        if (v) {
          newValues[field.id] = v;
          changed = true;
        }
      }
    });
    if (changed) {
      setPersonalizationValues(newValues);
      setAddressSelections(newAddrSelections);
    }
  }, [personalizationFields, effectiveUser, effectiveAddresses]);

  // Debounced admin user search
  useEffect(() => {
    if (!isAdminUser) return;
    if (!userSearch || userSearch.trim().length < 2) {
      setUserSearchResults([]);
      return;
    }
    setSearchingUsers(true);
    const handle = setTimeout(() => {
      UserService.searchSubsiteUsers(userSearch, 10)
        .then(resp => setUserSearchResults((resp && resp.data) || []))
        .catch(e => {
          console.error("Error searching users:", e);
          setUserSearchResults([]);
        })
        .finally(() => setSearchingUsers(false));
    }, 350);
    return () => clearTimeout(handle);
  }, [userSearch, isAdminUser]);

  // Compute prefilled values + default address selections for a given user/addresses.
  const computePrefills = (sourceUser, sourceAddresses) => {
    const values = {};
    const addrSel = {};
    (personalizationFields || []).forEach(field => {
      values[field.id] = '';
      if (!field.userProfileField) return;
      if (field.userProfileField === 'address') {
        if (sourceAddresses && sourceAddresses.length > 0) {
          const defaultAddr = sourceAddresses[0];
          addrSel[field.id] = defaultAddr.id;
          values[field.id] = formatAddress(defaultAddr);
        }
      } else {
        values[field.id] = getProfileValue(field.userProfileField, sourceUser, null);
      }
    });
    return { values, addrSel };
  };

  const selectImpersonatedUser = async (u) => {
    try {
      const resp = await UserService.getAddressesForUser(u.id);
      const data = (resp && resp.data) || {};
      const newUser = {
        id: u.id,
        firstname: data.firstname ?? u.firstname,
        lastname: data.lastname ?? u.lastname,
        email: data.email ?? u.email,
        phone: data.phone ?? u.phone
      };
      const newAddresses = data.addresses || [];
      const { values, addrSel } = computePrefills(newUser, newAddresses);
      setImpersonatedUser(newUser);
      setImpersonatedAddresses(newAddresses);
      setPersonalizationValues(values);
      setAddressSelections(addrSel);
      setUserSearchResults([]);
      setUserSearch("");
    } catch (e) {
      console.error("Error loading user for impersonation:", e);
    }
  };

  const clearImpersonatedUser = () => {
    const { values, addrSel } = computePrefills(user, userAddresses);
    setImpersonatedUser(null);
    setImpersonatedAddresses([]);
    setPersonalizationValues(values);
    setAddressSelections(addrSel);
  };

  const handlePersonalizationChange = (fieldId, value) => {
    setPersonalizationValues({ ...personalizationValues, [fieldId]: value });
  };

  const handleAddressSelectionChange = (fieldId, addressId) => {
    const addr = effectiveAddresses.find(a => String(a.id) === String(addressId));
    setAddressSelections({ ...addressSelections, [fieldId]: addressId });
    setPersonalizationValues({
      ...personalizationValues,
      [fieldId]: formatAddress(addr)
    });
  };

  const validatePersonalization = () => {
    for (const field of personalizationFields) {
      if (field.required && !personalizationValues[field.id]) {
        return { valid: false, message: `Please fill in ${field.name}` };
      }
    }
    return { valid: true };
  };

  const getPrice = () => {
    if (!printItem) return 0;

    if (printItem.pricingType === PRICING_TYPES.QUANTITY && printItem.print_item_quantity_prices?.length > 0) {
      // Find the exact quantity tier - price IS the total, not per unit
      const tier = printItem.print_item_quantity_prices.find(qp => qp.quantity === quantity);
      if (tier) {
        return tier.price;
      }
      // Fallback to first tier
      return printItem.print_item_quantity_prices[0].price;
    }

    return printItem.price || 0;
  };

  const getTotalPrice = () => {
    let personalizationSurcharge = 0;
    if (!orderBlank) {
      personalizationFields.forEach(field => {
        const val = personalizationValues[field.id];
        if (val && String(val).trim() && field.price != null && field.price !== '') {
          personalizationSurcharge += parseFloat(field.price) || 0;
        }
      });
    }
    // For quantity pricing, the tier price IS the total price
    if (printItem?.pricingType === PRICING_TYPES.QUANTITY) {
      return getPrice() + personalizationSurcharge * quantity;
    }
    // For flat pricing, multiply by quantity
    return (getPrice() + personalizationSurcharge) * quantity;
  };

  const handleAddToCart = async () => {
    if (!printItem) return;

    if (!orderBlank) {
      const validation = validatePersonalization();
      if (!validation.valid) {
        setAlertMessage(validation.message);
        setAlertVariant('danger');
        setShowAlert(true);
        setTimeout(() => setShowAlert(false), 4000);
        return;
      }
    }

    const personalization = orderBlank ? [] : personalizationFields.map(field => ({
      fieldId: field.id,
      fieldName: field.name,
      value: personalizationValues[field.id] || ''
    }));

    setAdding(true);
    try {
      await dispatch(addPrintItemToOrder({
        order: cartItems,
        item: {
          printItemId: printItem.id,
          quantity: quantity,
          personalization
        }
      })).unwrap();

      setAlertMessage('Item added to cart!');
      setAlertVariant('success');
      setShowAlert(true);
      setTimeout(() => setShowAlert(false), 3000);
      dispatch(toggleShowCart());
    } catch (err) {
      console.error("Error adding to cart:", err);
    } finally {
      setAdding(false);
    }
  };

  const getAllImages = () => {
    if (!printItem) return [];
    const images = [printItem.imageUrl];
    if (printItem.print_item_images) {
      printItem.print_item_images.forEach(img => images.push(img.imageUrl));
    }
    return images.filter(Boolean);
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" />
      </Container>
    );
  }

  if (error || !printItem) {
    return (
      <Container className="py-5">
        <Alert variant="danger">{error || "Item not found"}</Alert>
      </Container>
    );
  }

  const showPrice = loginStatus && currentSubsite.paymentOptions !== "itemlimit";
  const allImages = getAllImages();

  return (
    <Container className="py-4">
      {showAlert && (
        <Alert variant={alertVariant} onClose={() => setShowAlert(false)} dismissible>
          {alertMessage}
        </Alert>
      )}
      
      <Row>
        {/* Image Column */}
        <Col md={6}>
          <div className="p-2 mb-3 bg-secondary" >
            <Image
              src={getImageUrl(selectedImage)}
              fluid
              className="mx-auto d-block"
            />
          </div>
          {allImages.length > 1 && (
            <Row className="g-2">
              {allImages.map((img, idx) => (
                <Col xs={3} key={idx}>
                  <Image
                    src={getImageUrl(img)}
                    thumbnail
                    onClick={() => setSelectedImage(img)}
                    style={{
                      cursor: 'pointer',
                      opacity: selectedImage === img ? 1 : 0.6,
                      border: selectedImage === img ? '2px solid #007bff' : 'none'
                    }}
                  />
                </Col>
              ))}
            </Row>
          )}
        </Col>

        {/* Details Column */}
        <Col md={6}>
          <h2>{printItem.title}</h2>
          {printItem.code && <p className="text-muted">{printItem.code}</p>}

          {printItem.description && (
            <p>{printItem.description}</p>
          )}

          {printItem.htmlDescription && (
            <div dangerouslySetInnerHTML={{ __html: printItem.htmlDescription }} />
          )}

          {/* Pricing */}
          {showPrice && (
            <Card className="mb-3">
              <Card.Body>
                {printItem.pricingType === PRICING_TYPES.BILL_LATER ? (
                  <h4 className="text-muted">Billed Later</h4>
                ) : printItem.pricingType === PRICING_TYPES.QUANTITY ? (
                  <>
                    <h5>Quantity Pricing:</h5>
                    {printItem.print_item_quantity_prices?.map((qp, idx) => (
                      <div key={idx} style={{ fontSize: '0.9em' }}>
                        {qp.quantity} units: ${parseFloat(qp.price).toFixed(2)}
                      </div>
                    ))}
                  </>
                ) : (
                  <h4>${printItem.price?.toFixed(2) || '0.00'}</h4>
                )}
              </Card.Body>
            </Card>
          )}

          {/* Quantity Selection */}
          <Form.Group className="mb-3">
            <Form.Label>Quantity</Form.Label>
            {printItem.pricingType === PRICING_TYPES.QUANTITY && printItem.print_item_quantity_prices?.length > 0 ? (
              <Form.Select
                value={quantity}
                onChange={(e) => setQuantity(parseInt(e.target.value))}
                style={{ width: 200 }}
              >
                {printItem.print_item_quantity_prices.map((tier, index) => (
                  <option key={index} value={tier.quantity}>
                    {tier.quantity} units - ${parseFloat(tier.price).toFixed(2)}
                  </option>
                ))}
              </Form.Select>
            ) : (
              <Form.Select
                value={quantity}
                onChange={(e) => setQuantity(parseInt(e.target.value))}
                style={{ width: 120 }}
              >
                <option value="1">1</option>
                <option value="2">2</option>
                <option value="3">3</option>
                <option value="4">4</option>
                <option value="5">5</option>
                <option value="6">6</option>
                <option value="7">7</option>
                <option value="8">8</option>
                <option value="9">9</option>
                <option value="10">10</option>
              </Form.Select>
            )}
          </Form.Group>

          {showPrice && printItem.pricingType !== PRICING_TYPES.BILL_LATER && (
            <h4 className="mb-3">Total: ${getTotalPrice().toFixed(2)}</h4>
          )}

          {personalizationFields.length > 0 && (
            <div style={{ marginTop: 15, marginBottom: 15, padding: 10, border: '1px solid #ddd', borderRadius: 8, backgroundColor: '#f9f9f9' }}>
              <div style={{ fontWeight: 'bold', marginBottom: 10, fontSize: '1.1em' }}>
                Personalization
              </div>
              {isAdminUser && (
                <Form.Check
                  type="checkbox"
                  id="order-blank"
                  label="Order blank"
                  checked={orderBlank}
                  onChange={(e) => setOrderBlank(e.target.checked)}
                  className="mb-3"
                />
              )}
              {!orderBlank && isAdminUser && (
                <div style={{ marginBottom: 12, padding: 8, border: '1px dashed #999', borderRadius: 6, backgroundColor: '#fff' }}>
                  <Form.Label style={{ fontWeight: 500, marginBottom: 4 }}>
                    Order on behalf of (admin)
                  </Form.Label>
                  {impersonatedUser ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span>
                        Using profile of <strong>{[impersonatedUser.firstname, impersonatedUser.lastname].filter(Boolean).join(' ') || impersonatedUser.email}</strong>
                      </span>
                      <Button variant="outline-secondary" size="sm" onClick={clearImpersonatedUser}>
                        Use my own profile
                      </Button>
                    </div>
                  ) : (
                    <>
                      <Form.Control
                        type="text"
                        placeholder="Search users by name or email..."
                        value={userSearch}
                        onChange={(e) => setUserSearch(e.target.value)}
                      />
                      {searchingUsers && (
                        <div style={{ marginTop: 4, fontSize: '0.85em', color: '#666' }}>Searching…</div>
                      )}
                      {userSearchResults.length > 0 && (
                        <div style={{ marginTop: 4, maxHeight: 180, overflowY: 'auto', border: '1px solid #ddd', borderRadius: 4, backgroundColor: '#fff' }}>
                          {userSearchResults.map(u => (
                            <div
                              key={u.id}
                              onClick={() => selectImpersonatedUser(u)}
                              style={{ padding: '6px 8px', cursor: 'pointer', borderBottom: '1px solid #eee' }}
                            >
                              {[u.firstname, u.lastname].filter(Boolean).join(' ') || '(no name)'} — {u.email}
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
              {!orderBlank && personalizationFields.map(field => {
                const locked = !!field.userProfileField && (field.isEditable === false || field.isEditable === 0);
                const isAddressField = field.userProfileField === 'address';
                return (
                  <div key={field.id} style={{ marginBottom: 10 }}>
                    <Form.Label style={{ fontWeight: 500, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span>
                        {field.name}
                        {field.required && <span style={{ color: 'red' }}> *</span>}
                        {field.price != null && field.price !== '' && parseFloat(field.price) > 0 && (
                          <span style={{ color: '#666', fontWeight: 400, fontSize: '0.9em' }}>
                            {' '}+${parseFloat(field.price).toFixed(2)}
                          </span>
                        )}
                      </span>
                      {(field.deco_color?.colorName || field.decoColor?.colorName) && (
                        <span style={{ color: '#555', fontWeight: 400, fontSize: '0.9em' }}>
                          Color: {field.deco_color?.colorName || field.decoColor?.colorName}
                        </span>
                      )}
                    </Form.Label>
                    {isAddressField ? (
                      effectiveAddresses.length > 1 ? (
                        <Form.Select
                          value={addressSelections[field.id] || ''}
                          onChange={(e) => handleAddressSelectionChange(field.id, e.target.value)}
                        >
                          {effectiveAddresses.map(a => (
                            <option key={a.id} value={a.id}>
                              {formatAddress(a)}
                            </option>
                          ))}
                        </Form.Select>
                      ) : (
                        <Form.Control
                          type="text"
                          value={personalizationValues[field.id] || ''}
                          onChange={(e) => handlePersonalizationChange(field.id, e.target.value)}
                          disabled={locked}
                          readOnly={locked}
                        />
                      )
                    ) : field.fieldType === 'text' ? (
                      <Form.Control
                        type="text"
                        placeholder={field.placeholder || ''}
                        maxLength={field.maxLength || 100}
                        value={personalizationValues[field.id] || ''}
                        onChange={(e) => handlePersonalizationChange(field.id, e.target.value)}
                        disabled={locked}
                        readOnly={locked}
                      />
                    ) : (
                      <Form.Select
                        value={personalizationValues[field.id] || ''}
                        onChange={(e) => handlePersonalizationChange(field.id, e.target.value)}
                        disabled={locked}
                      >
                        <option value="">Select...</option>
                        {(field.options || []).map((opt, idx) => (
                          <option key={idx} value={opt}>{opt}</option>
                        ))}
                      </Form.Select>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <Button
            variant="primary"
            size="lg"
            onClick={handleAddToCart}
            disabled={adding}
          >
            {adding ? <Spinner animation="border" size="sm" /> : 'Add to Cart'}
          </Button>
        </Col>
      </Row>
    </Container>
  );
};

export default PrintItemDetails;

